import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Alert, Button, Card, Input, Select, Switch } from 'antd';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  CompanyOperationalStatus,
  ORDER_APPROVAL_TIME_STEP_OPTIONS,
  ORDER_EXTRA_TIME_OPTIONS,
  SCHEDULED_ORDERS_TIME_SLOT_INTERVAL_OPTIONS,
} from '../../services/exports/Constants';
import usePermissions, { ManagerPermission } from '../../hooks/auth/usePermissions';
import { ScreenEnum } from '../../hooks/navigation/Screens';
import { isInFirstLaunchOnboarding } from '../../services/tenant/plgOnboarding';
import useHistory from '../../hooks/navigation/useHistory';
import useCanAccessOnboardingWizard from '../../hooks/auth/useCanAccessOnboardingWizard';

export default function GeneralTab({
  data,
  feedback,
  loading,
  onChange,
  onSave,
  setShowConfirmLiveOrdersSwitchDrawer,
  onRequestGoLive,
  goLivePending = false,
}) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Takeout:GeneralTab' });

  const { can, isMerchant } = usePermissions();
  const { serviceProvider } = useSelector((state) => state.currentServiceProvider);

  const history = useHistory();
  const canOpenWizard = useCanAccessOnboardingWizard();

  const onToggleLiveOrders = () => {
    // Before its first launch a PLG restaurant goes live from the wizard's Go Live
    // step, which checks every step before it — the toggle takes the partner there.
    if (canOpenWizard && isInFirstLaunchOnboarding(serviceProvider, data)) {
      history.push(ScreenEnum.RestaurantOnboarding);

      return;
    }

    // Going live may be a purchase (the licence paywall), so the owning view
    // decides what opens first; pausing always goes straight to the drawer.
    if (onRequestGoLive && data?.operational_status !== CompanyOperationalStatus.Live) {
      onRequestGoLive();

      return;
    }

    setShowConfirmLiveOrdersSwitchDrawer(true);
  };

  return (
    <div className="mt-lg">
      {can(ManagerPermission.LaunchCompanies) && (
        <Card className="mb-md">
          <div className="text-base font-semibold mb-md">{t('sections.asap_orders.fields.has_live_orders.label')}</div>
          <div>
            {data?.operational_status === CompanyOperationalStatus.Suspended && (
              <Alert
                showIcon
                type="warning"
                className="mb-md"
                title={t('feedback.restaurant_suspended.title')}
                description={
                  <Trans
                    t={t}
                    components={{
                      a: <Link to={`${ScreenEnum.BillingSubscription}`} />,
                    }}
                  >
                    feedback.restaurant_suspended.description
                  </Trans>
                }
              />
            )}
            <div className="mb-md">
              <Switch
                id="has-live-orders"
                disabled={
                  data?.operational_status === CompanyOperationalStatus.Suspended ||
                  (data?.operational_status === CompanyOperationalStatus.Live &&
                    !can(ManagerPermission.DisableLiveOrders))
                }
                checked={data?.operational_status === CompanyOperationalStatus.Live}
                // The licence gate may wait for the entitlements before it decides.
                loading={goLivePending}
                onChange={onToggleLiveOrders}
              />
              <label className="ml-4 align-bottom cursor-pointer" htmlFor="has-live-orders">
                {t('sections.asap_orders.fields.has_live_orders.label')}
              </label>
              <div className="text-xs text-gray-400 mt-xs">
                {t('sections.asap_orders.fields.has_live_orders.tooltip')}
              </div>
              <div className="text-sm text-gray-500">{feedback?.errors?.has_live_orders}</div>
            </div>
          </div>
        </Card>
      )}
      <Card className="mb-md">
        <div className="text-base font-semibold mb-md">{t('sections.asap_orders.title')}</div>
        <div>
          <div className="mb-md">
            <Switch
              id="has-asap-orders"
              checked={data?.has_asap_orders}
              onChange={(checked) => {
                if (!checked && !data?.has_scheduled_orders) {
                  return toast.error(t('toasts.at_least_one_order_method_required'));
                }

                return onChange('has_asap_orders', checked);
              }}
            />
            <span className="ml-4 my-auto align-bottom cursor-pointer">
              <Trans t={t}>sections.asap_orders.fields.has_asap_orders.label</Trans>
            </span>
            <div className="text-xs text-gray-400 mt-xs">
              {t('sections.asap_orders.fields.has_asap_orders.tooltip')}
            </div>
            <div className="text-sm text-gray-500">{feedback?.errors?.has_asap_orders}</div>
          </div>
          <Button type="primary" loading={loading} disabled={loading} onClick={onSave} className="my-4">
            {t('buttons.save')}
          </Button>
        </div>
      </Card>
      <Card className="mb-md">
        <div className="text-base font-semibold mb-md">{t('sections.scheduled_orders.title')}</div>
        <div>
          <div className="mb-md">
            <Switch
              id="has-scheduled-orders"
              checked={data?.has_scheduled_orders}
              onChange={(checked) => {
                if (!checked && !data?.has_asap_orders) {
                  return toast.error(t('toasts.at_least_one_order_method_required'));
                }

                return onChange('has_scheduled_orders', checked);
              }}
            />
            <label className="ml-4 align-bottom cursor-pointer" htmlFor="has-scheduled-orders">
              {t('sections.scheduled_orders.fields.has_scheduled_orders.label')}
            </label>
            <div className="text-sm text-gray-500">{feedback?.errors?.has_scheduled_orders}</div>
          </div>
          {data?.has_scheduled_orders && (
            <>
              <div className="mb-md">
                <Switch
                  id="has-pre-order"
                  checked={data?.has_pre_order}
                  onChange={(checked) => onChange('has_pre_order', checked)}
                />
                <label className="ml-4 align-bottom cursor-pointer" htmlFor="has-pre-order">
                  {t('sections.scheduled_orders.fields.has_pre_order.label')}
                </label>
                <div className="text-sm text-gray-500">{feedback?.errors?.has_pre_order}</div>
              </div>
              {data.has_pre_order && (
                <div className="mb-md">
                  <label className="cursor-pointer" htmlFor="max-pre-order-days">
                    {t('sections.scheduled_orders.fields.max_pre_order_days.label')}
                  </label>
                  <Input
                    id="max-pre-order-days"
                    type="number"
                    placeholder={t('sections.scheduled_orders.fields.max_pre_order_days.placeholder')}
                    value={data?.max_pre_order_days}
                    onChange={(e) => onChange('max_pre_order_days', e.target.value)}
                    onWheel={(e) => e.target.blur()}
                  />
                  <div className="text-sm text-gray-500">{feedback?.errors?.max_pre_order_days}</div>
                </div>
              )}
              <div className="mb-md">
                <Switch
                  id="has-scheduled-order-reminders"
                  checked={data?.has_scheduled_order_reminders}
                  onChange={(checked) => onChange('has_scheduled_order_reminders', checked)}
                />
                <label className="ml-4 align-bottom cursor-pointer" htmlFor="has-scheduled-order-reminders">
                  {t('sections.scheduled_orders.fields.has_scheduled_order_reminders.label')}
                </label>
                <div className="text-sm text-gray-500">{feedback?.errors?.has_scheduled_order_reminders}</div>
              </div>
              <div className="mb-md">
                <label htmlFor="scheduled-orders-time-slot-interval">
                  {t('sections.scheduled_orders.fields.scheduled_orders_time_slot_interval.label')}
                </label>
                <Select
                  id="scheduled-orders-time-slot-interval"
                  className="w-full"
                  value={data?.scheduled_orders_time_slot_interval}
                  onChange={(value) => onChange('scheduled_orders_time_slot_interval', value)}
                  options={SCHEDULED_ORDERS_TIME_SLOT_INTERVAL_OPTIONS.map((item) => ({
                    value: item,
                    label: t('sections.scheduled_orders.fields.scheduled_orders_time_slot_interval.option', {
                      value: item,
                    }),
                  }))}
                />
                <div className="text-sm text-gray-500">{feedback?.errors?.scheduled_orders_time_slot_interval}</div>
              </div>
              {!isMerchant && (
                <>
                  <div className="mb-md">
                    <Switch
                      id="has-scheduled-orders-time-slot-order-limit"
                      checked={data?.has_scheduled_orders_time_slot_order_limit}
                      onChange={(checked) => {
                        if (!checked) {
                          onChange('scheduled_orders_time_slot_order_limit', null);
                        }

                        onChange('has_scheduled_orders_time_slot_order_limit', checked);
                      }}
                    />
                    <label
                      className="ml-4 align-bottom cursor-pointer"
                      htmlFor="has-scheduled-orders-time-slot-order-limit"
                    >
                      {t('sections.scheduled_orders.fields.has_scheduled_orders_time_slot_order_limit.label')}
                    </label>
                  </div>
                  {data?.has_scheduled_orders_time_slot_order_limit && (
                    <div className="mb-md">
                      <label className="cursor-pointer" htmlFor="scheduled-orders-time-slot-order-limit">
                        {t('sections.scheduled_orders.fields.scheduled_orders_time_slot_order_limit.label')}
                      </label>
                      <Input
                        id="scheduled-orders-time-slot-order-limit"
                        type="number"
                        placeholder={t(
                          'sections.scheduled_orders.fields.scheduled_orders_time_slot_order_limit.placeholder',
                        )}
                        value={data?.scheduled_orders_time_slot_order_limit}
                        onChange={(e) => onChange('scheduled_orders_time_slot_order_limit', e.target.value)}
                        onWheel={(e) => e.target.blur()}
                      />
                      <div className="text-sm text-gray-500">
                        {feedback?.errors?.scheduled_orders_time_slot_order_limit}
                      </div>
                    </div>
                  )}
                </>
              )}

              <div className="mb-md">
                <Switch
                  id="has_order_instructions"
                  checked={data?.has_order_instructions}
                  onChange={(checked) => {
                    onChange('has_order_instructions', checked);
                  }}
                />
                <label className="ml-4 align-bottom cursor-pointer" htmlFor="has_order_instructions">
                  {t('sections.scheduled_orders.fields.order_instructions_title.label')}
                </label>
              </div>
              {data.has_order_instructions && (
                <div className="mb-md">
                  <label className="cursor-pointer" htmlFor="order_instructions_title">
                    {t('sections.scheduled_orders.fields.order_instructions_title.label')}
                  </label>
                  <Input
                    id="order_instructions_title"
                    placeholder={t('sections.scheduled_orders.fields.order_instructions_title.placeholder')}
                    value={data?.order_instructions_title}
                    onChange={(e) => onChange('order_instructions_title', e.target.value)}
                  />
                </div>
              )}
              <div className="mb-md">
                <label className="cursor-pointer" htmlFor="scheduled_order_option_title">
                  {t('sections.scheduled_orders.fields.scheduled_order_option_title.label')}
                </label>
                <Input
                  id="scheduled_order_option_title"
                  placeholder={t('sections.scheduled_orders.fields.scheduled_order_option_title.placeholder')}
                  value={data?.scheduled_order_option_title}
                  onChange={(e) => onChange('scheduled_order_option_title', e.target.value)}
                />
              </div>
            </>
          )}
          <Button type="primary" loading={loading} disabled={loading} onClick={onSave} className="my-4">
            {t('buttons.save')}
          </Button>
        </div>
      </Card>
      <Card className="mb-md">
        <div className="text-base font-semibold mb-md">{t('sections.other.title')}</div>
        <div>
          <div className="mb-md">
            <label htmlFor="order-approval-time-step">
              {t('sections.other.fields.order_approval_time_step.label')}
            </label>
            <Select
              id="order-approval-time-step"
              className="w-full"
              value={data?.order_approval_time_step}
              onChange={(value) => onChange('order_approval_time_step', value)}
              options={ORDER_APPROVAL_TIME_STEP_OPTIONS.map((item) => ({
                value: item,
                label: t('sections.other.fields.order_approval_time_step.option', { value: item }),
              }))}
            />
            <div className="text-sm text-gray-500">{feedback?.errors?.order_approval_time_step}</div>
          </div>
          <div className="mb-md">
            <label htmlFor="order-confirm-time-extra-minutes">
              {t('sections.other.fields.order_confirmation_time_extra_minutes.label')}
            </label>
            <Select
              id="order-confirm-time-extra-minutes"
              className="w-full"
              value={data?.order_confirmation_time_extra_minutes}
              onChange={(value) => onChange('order_confirmation_time_extra_minutes', value)}
              options={ORDER_EXTRA_TIME_OPTIONS.map((item) => ({
                value: item,
                label: t('sections.other.fields.order_confirmation_time_extra_minutes.option', { value: item }),
              }))}
            />
            <div className="text-sm text-gray-500">{feedback?.errors?.order_confirmation_time_extra_minutes}</div>
          </div>
          <Button type="primary" loading={loading} disabled={loading} onClick={onSave} className="my-4">
            {t('buttons.save')}
          </Button>
        </div>
      </Card>
    </div>
  );
}
